import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import BusinessCard from "@/components/BusinessCard";
import BusinessCardModal from "@/components/BusinessCardModal";
import { buildVCard, downloadVCard } from "@/lib/vcard";

export default function Directory() {
  const { user } = useAuth();
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/directory").then((r) => setPeople(r.data)).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return people;
    return people.filter((p) => {
      const pr = p.profile || {};
      return [p.name, p.email, pr.title, pr.department, pr.company, pr.work_email]
        .filter(Boolean).some((v) => v.toLowerCase().includes(s));
    });
  }, [people, q]);

  const saveAll = () => {
    const combined = people
      .map((p) => buildVCard({ name: p.name, email: p.email, profile: p.profile || {} }))
      .join("\r\n");
    downloadVCard(combined, "Renova_team_directory.vcf");
    toast.success(`Saved ${people.length} contacts`);
  };

  return (
    <div className="p-6 sm:p-8 max-w-[1400px]">
      <header className="mb-6 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-wider font-semibold text-gray-400">Networking</p>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight mt-1">Company directory</h1>
          <p className="text-sm text-gray-500 mt-1">Everyone at Renova — tap a card to view, share or save their contact.</p>
        </div>
        <Button onClick={saveAll} variant="outline" data-testid="save-all-btn">
          <Icons.Download className="w-4 h-4 mr-1.5" /> Save all contacts
        </Button>
      </header>

      <div className="relative mb-6 max-w-md">
        <Icons.Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, title, department…"
          className="pl-9" data-testid="directory-search" />
      </div>

      {loading ? (
        <div className="text-gray-400 text-sm">Loading directory…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-gray-400 text-sm py-16">No people match "{q}".</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 items-start" data-testid="directory-grid">
          {filtered.map((p) => (
            <button key={p.id} onClick={() => setSelected(p)} data-testid={`directory-card-${p.id}`}
              className="text-left hover:-translate-y-0.5 hover:shadow-md transition-all rounded-2xl">
              <BusinessCard name={p.name} email={p.email} role={p.role} profile={p.profile || {}} />
            </button>
          ))}
        </div>
      )}

      {selected && (
        <BusinessCardModal
          open={!!selected}
          onOpenChange={(o) => !o && setSelected(null)}
          name={selected.name}
          email={selected.email}
          role={selected.role}
          profile={selected.profile || {}}
          title={selected.id === user?.id ? "My digital business card" : `${selected.name}'s card`}
        />
      )}
    </div>
  );
}
